import React from 'react';
import { Car } from 'lucide-react';
import { colors, borderRadius } from '../../styles/tokens';
import { useBlockEditor } from '../../hooks/useBlockEditor';
import { EditableField } from '../editing';

export default function CarCard({ block, onRemove, quoteId, onBlockUpdate }) {
  const editable = !!quoteId;
  const { saving, updateField, updateDetail, isSaved } =
    useBlockEditor(quoteId, block, onBlockUpdate);

  const details = block.details || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const E = (value, onSave, opts = {}) => {
    if (!editable) return opts.fallback || value || opts.placeholder || '-';
    return (
      <EditableField
        value={value}
        onSave={onSave}
        type={opts.type || 'text'}
        saving={saving}
        saved={opts.saved}
        placeholder={opts.placeholder || '-'}
        style={opts.style}
        validate={opts.validate}
      />
    );
  };

  return (
    <div style={{
      backgroundColor: colors.card, border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md, padding: '1.5rem', position: 'relative',
    }}>
      <button onClick={() => onRemove(block.id)} style={{
        position: 'absolute', top: '1rem', right: '1rem',
        background: 'none', border: 'none', cursor: 'pointer',
        color: colors.text.muted, fontSize: '1.25rem',
      }}>×</button>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{
          width: '48px', height: '48px', backgroundColor: `${colors.primary}10`,
          borderRadius: borderRadius.sm, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <Car size={24} color={colors.primary} />
        </div>

        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.primary, margin: '0 0 1rem 0' }}>
            {E(block.title, (v) => updateField('title', v), { saved: isSaved('title'), fallback: block.title })}
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Local retirada */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Local de retirada</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.pickup_location, (v) => updateDetail('pickup_location', v), {
                  saved: isSaved('pickup_location'), fallback: details.pickup_location || '-',
                })}
              </p>
            </div>

            {/* Data retirada */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Data de retirada</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.pickup_date || block.date, (v) => updateDetail('pickup_date', v), {
                  type: 'date', saved: isSaved('pickup_date'),
                  fallback: formatDate(details.pickup_date || block.date),
                })}
              </p>
            </div>

            {/* Local devolução */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Local de devolução</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.dropoff_location || details.pickup_location, (v) => updateDetail('dropoff_location', v), {
                  saved: isSaved('dropoff_location'), fallback: details.dropoff_location || details.pickup_location || '-',
                })}
              </p>
            </div>

            {/* Data devolução */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Data de devolução</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.dropoff_date, (v) => updateDetail('dropoff_date', v), {
                  type: 'date', saved: isSaved('dropoff_date'),
                  fallback: formatDate(details.dropoff_date),
                  validate: (v) => {
                    const pickup = details.pickup_date || block.date;
                    if (pickup && v < pickup) return 'Antes da retirada';
                    return null;
                  },
                })}
              </p>
            </div>

            <div></div>

            {/* Valor */}
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Valor</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.accent, margin: 0 }}>
                {E(block.price || 0, (v) => updateField('price', v), {
                  type: 'currency', saved: isSaved('price'),
                  validate: (v) => v < 0 ? 'Preço inválido' : null,
                  fallback: `R$ ${(block.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
